import React, { useState, useEffect } from 'react';
import Udesk from 'Udesk';
import {
    Table,
    Form,
    Input,
    Button,
    DatePicker,
    Space,
    Switch,
    message,
    Modal,
    Select,
} from 'udesk-ui';
import UdeskPagination from 'Component/common/udesk-pagination';
import type { ColumnsType } from 'udesk-ui/es/table';
import { getIntelligentPartnerCustomerPersonas } from 'src/api/intelligentPartnerCustomerPersonas';
import type {
    IntelligentPartnerCustomerPersonaFoundResponse,
    IntelligentPartnerDialogNodeAiTemplateFoundResponse,
} from 'src/api/types';
import { putReviewIntelligentPartnerNodeInputSceneConfigManageRoleEnableById } from 'src/api/review/intelligentPartner/node/inputSceneConfig/manage/roleEnable/{id}';
import { deleteReviewIntelligentPartnerNodeInputSceneConfigManageDeleteSceneByIdById } from 'src/api/review/intelligentPartner/node/inputSceneConfig/manage/deleteSceneById/{id}';
import { useSdkOptions } from 'src/util/hook';
import UdeskLocales from 'UdeskLocales';

const DialogueManage: React.FC<any> = ({ history }) => {
    const sdkOptions = useSdkOptions();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState<
        IntelligentPartnerDialogNodeAiTemplateFoundResponse[]
    >([]);
    const [allCustomerPortrait, setAllCustomerPortrait] = useState<
        IntelligentPartnerCustomerPersonaFoundResponse[]
    >([]);
    const [searchParams, setSearchParams] = useState<any>({});
    const [form] = Form.useForm();
    const [paging, setPaging] = useState({
        pageNum: 1,
        pageSize: 10,
        total: 0,
    });

    const requestAllCustomerPortrait = () => {
        getIntelligentPartnerCustomerPersonas().then((resp) => {
            setAllCustomerPortrait(resp.data ?? []);
        });
    };

    // 表格列配置
    const columns: ColumnsType<IntelligentPartnerDialogNodeAiTemplateFoundResponse> = [
        {
            title: /* 名称 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.name,
            dataIndex: 'templateName',
            key: 'templateName',
        },
        {
            title: /* 陪练对象(学员) */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.accompanyingTrainingObjectCadet,
            dataIndex: 'sparringPartner',
            key: 'sparringPartner',
        },
        {
            title: /* 建议角色(机器人) */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.proposedRolesRobots,
            dataIndex: 'simulatedRoleName',
            key: 'simulatedRoleName',
        },
        {
            title: /* 创建时间 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.creationTime,
            dataIndex: 'createTime',
            key: 'createTime',
        },
        {
            title: /* 备注 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.remarks,
            dataIndex: 'remark',
            key: 'remark',
            ellipsis: true,
        },
        {
            title: /* 启用状态 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.enabledStatus,
            dataIndex: 'enableStatus',
            key: 'enableStatus',
            render: (status: number, record) => (
                <Switch
                    checked={status === 0}
                    onChange={(checked) => handleStatusChange(checked, record)}
                />
            ),
        },
        {
            title: /* 操作 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.operation,
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button type="link" onClick={() => handleEdit(record)}>{/* 编辑 */}{UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.edit}</Button>
                    <Button type="link" danger onClick={() => handleDelete(record)}>{/* 删除 */}{UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.delete}</Button>
                </Space>
            ),
        },
    ];

    // 获取列表数据
    const fetchList = async () => {
        setLoading(true);
        try {
            const url = Udesk.business.apiPath.concatApiPath(
                `/review/intelligentPartner/node/inputSceneConfig/template/findScenePageList?pageNum=${paging.pageNum}&pageSize=${paging.pageSize}`,
                sdkOptions
            );
            const params: any = {
                ...searchParams,
            };
            if (searchParams?.dateRange) {
                params.startTime = searchParams.dateRange[0].format('YYYY-MM-DD') + 'T00:00:00';
                params.endTime = searchParams.dateRange[1].format('YYYY-MM-DD') + 'T23:59:59';
            }
            delete params.dateRange;
            await Udesk.ajax.post(url, params).then(
                (resp) => {
                    setDataSource(resp.data || []);
                    setPaging(resp.paging as any);
                },
                (reason) => {}
            );
        } catch (error) {
            message.error(/* 获取列表失败 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.failedToGetList);
        } finally {
            setLoading(false);
        }
    };

    // 分页变化
    const pageChanged = (pageNum: number, pageSize: number) => {
        setPaging((prev) => ({
            ...prev,
            pageNum,
            pageSize,
        }));
    };

    // 搜索
    const handleSearch = () => {
        setPaging({
            ...paging,
            pageNum: 1,
        });
        setSearchParams(form.getFieldsValue());
    };

    // 重置搜索
    const handleReset = () => {
        form.resetFields();
        setPaging({
            ...paging,
            pageNum: 1,
        });
        setSearchParams({});
    };

    // 状态切换
    const handleStatusChange = async (checked: boolean, record: any) => {
        setLoading(true);
        try {
            await putReviewIntelligentPartnerNodeInputSceneConfigManageRoleEnableById(
                {
                    enableStatus: checked ? 0 : 1,
                },
                {
                    segments: {
                        id: record.id!,
                    },
                }
            );
            message.success(/* 状态更新成功 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.statusUpdateSuccessful);
            fetchList();
        } catch (error) {
            message.error(/* 状态更新失败 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.statusUpdateFailed);
        } finally {
            setLoading(false);
        }
    };

    // 新建角色
    const handleCreate = () => {
        const routeOptions = {
            history: history,
            routeName: 'coachTemplateManageDialogueNew',
        };
        Udesk.ui.routing.transitionTo(routeOptions);
    };

    // 编辑场景
    const handleEdit = (record: IntelligentPartnerDialogNodeAiTemplateFoundResponse) => {
        console.log('Edit record:', record);
        const routeOptions = {
            history: history,
            routeName: 'coachTemplateManageDialogueEdit',
            pathParams: { id: record.id! },
            state: {
                record,
            },
        };
        Udesk.ui.routing.transitionTo(routeOptions);
    };

    // 删除场景
    const handleDelete = (record: IntelligentPartnerDialogNodeAiTemplateFoundResponse) => {
        Modal.confirm({
            title: /* 是否确认删除？ */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.areYouSureYouWantToDelete,
            okText: /* 确认 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.confirm,
            okType: 'danger',
            cancelText: /* 取消 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.cancel,
            onOk: async () => {
                try {
                    setLoading(true);
                    await deleteReviewIntelligentPartnerNodeInputSceneConfigManageDeleteSceneByIdById(
                        {
                            segments: {
                                id: record.id!,
                            },
                        }
                    );
                    Udesk.ui.notify.success(/* 删除成功 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.deletedSuccessfully);
                    fetchList();
                } catch (error) {
                    Udesk.ui.notify.error(/* 删除失败 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.deleteFailed);
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, paging.pageNum, paging.pageSize]);

    useEffect(() => {
        requestAllCustomerPortrait();
    }, []);

    return (
        <div className="dialogue-manage">
            {/* 搜索区域 */}
            <div className="search-area">
                <Form form={form} layout="inline" onFinish={handleSearch}>
                    <Form.Item name="templateName" label={/* 场景名称 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.sceneName}>
                        <Input placeholder={/* 请输入 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.pleaseEnter} />
                    </Form.Item>
                    <Form.Item name="sparringPartner" label={/* 陪练对象(学员) */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.accompanyingTrainingObjectCadet}>
                        <Input placeholder={/* 请输入陪练对象(学员) */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.pleaseEnterThePartnerstudent} />
                    </Form.Item>
                    <Form.Item label={/* 建议角色(机器人) */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.proposedRolesRobots} name="simulatedRole">
                        <Select placeholder={/* 请输入角色名称 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.pleaseEnterARoleName} allowClear style={{ width: 200 }}>
                            {allCustomerPortrait.map((item) => (
                                <Select.Option key={item.id} value={item.id!!}>
                                    {item.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="remark" label={/* 备注 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.remarks}>
                        <Input placeholder={/* 请输入备注 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.pleaseEnterComments} />
                    </Form.Item>
                    <Form.Item name="dateRange" label={/* 创建时间 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.creationTime}>
                        <DatePicker.RangePicker placeholder={[/* 开始日期 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.startDate, /* 结束日期 */UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.endDate]} />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button onClick={handleReset}>{/* 重置 */}{UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.reset}</Button>
                            <Button type="primary" onClick={handleSearch}>{/* 筛选 */}{UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.filter}</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </div>

            {/* 表格区域 */}
            <div className="table-area">
                <div className="table-header">
                    <Space>
                        <Button type="primary" onClick={handleCreate}>{/* 新建 */}{UdeskLocales['current'].pages.coach.templateManage.dialogueManage.list.new}</Button>
                    </Space>
                </div>

                <Table
                    loading={loading}
                    columns={columns}
                    dataSource={dataSource}
                    rowKey="id"
                    pagination={false}
                />

                {/* 分页器 */}
                <div className="pagination-wrapper">
                    {dataSource.length > 0 && (
                        <UdeskPagination
                            current={paging.pageNum}
                            pageSize={paging.pageSize}
                            total={paging.total}
                            onChange={pageChanged}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DialogueManage;
